import { useMemo } from 'react'
import { GenericObject } from '~/prix/types/type'
import { Layer } from '../variable/spaceTime.types'
import { VariableAndOptionalResult } from '../variable/variables.typing'
import customInformation from './customInformation'
import GeneralPageInformation from './generalPage'
import defaultGeneralInformationSchema from './schemas/default/generalInformationSchema'
import defaultLocationInformationSchema from './schemas/default/locationInformationSchema'
import getSchemaOrFallback, { hasSchema, resolveSectionTitle } from './utils/getSchemaOrFallback'
import formatBasedOnType from '~/prix/utils/formatBasedOnType'
import { detectMaskFromFieldName } from './informationMasks'
import { deepEqual } from '~/prix/utils/types/deepCompare'

export interface InfoProps {
  displayName: string
  value: string | null
  mask: string
  notAvailable: string
}

export type ObjectInfo = { [key: string]: InfoProps }

interface ModalInformationProps {
  isOpen: boolean
  onClose: () => void
  item: GenericObject
  isLoading: boolean
  detailKey: any // TODO: aplicar tipo correto
  layer: Layer
  coordinates?: {
    lat: number
    lng: number
  }
}

export const ModalInformation = ({
  isOpen,
  onClose,
  item,
  isLoading,
  layer,
  coordinates,
}: ModalInformationProps) => {
  const variable = layer?.variable as VariableAndOptionalResult

  const createFieldFromShape = (fieldName: string) => {
    const shape = variable?.shape?.shape
    if (!shape || typeof shape !== 'object' || Array.isArray(shape)) return null

    const shapeField = (shape as Record<string, any>)[fieldName]
    if (!shapeField) return null

    const mask = detectMaskFromFieldName(fieldName, shapeField.type)

    return {
      displayName: shapeField.label || fieldName,
      notAvailable: 'Não informado (a)',
      mask,
      canCopy: false,
    }
  }

  const processSchema = (schema: any) => {
    const processedSchema = { ...schema } as Record<string, any>

    Object.keys(processedSchema).forEach(fieldName => {
      const shape = variable?.shape?.shape
      if (shape && typeof shape === 'object' && !Array.isArray(shape)) {
        const shapeField = (shape as Record<string, any>)[fieldName]

        processedSchema[fieldName] = {
          ...processedSchema[fieldName],
          formatter: (value: any) => {
            return formatBasedOnType(value, shapeField, null)
          },
        }
      }
    })

    return processedSchema
  }

  const includeMissingFields = (
    originalSchema: Record<string, any>,
    defaultSchema: Record<string, any>,
    allFields: string[],
  ) => {
    const schema = { ...originalSchema } as Record<string, any>
    if (!deepEqual(schema, defaultSchema)) return originalSchema
    allFields.forEach(fieldName => {
      if (fieldName.includes('latitude') || fieldName.includes('longitude') || fieldName.includes('originalId')) return
      if (!schema[fieldName]) {
        const dynamicField = createFieldFromShape(fieldName)
        if (dynamicField) {
          schema[fieldName] = dynamicField
        }
      }
    })

    return processSchema(schema)
  }

  const information = useMemo(() => {
    if (item === undefined) return undefined
    const newItem = { ...item }
    const actionDefinitionKey = variable?.key

    const originalFirst = getSchemaOrFallback(
      `${actionDefinitionKey}GeneralInformationSchema`,
      defaultGeneralInformationSchema,
    )
    const first = includeMissingFields(
      originalFirst,
      defaultGeneralInformationSchema,
      variable?.basicSelect || [],
    )

    const second = getSchemaOrFallback(
      `${actionDefinitionKey}LocationInformationSchema`,
      defaultLocationInformationSchema,
      '', //
    )

    const third = getSchemaOrFallback(
      `${actionDefinitionKey}InformationSchema`,
      defaultLocationInformationSchema,
      actionDefinitionKey, //
    )

    const originalFourth = getSchemaOrFallback(`${actionDefinitionKey}MoreInformationSchema`, {})
    const fourth = includeMissingFields(originalFourth, {}, variable?.detailedSelect || [])

    return customInformation({
      item: newItem,
      schemas: {
        first,
        second,
        third:  processSchema({ ...third }),
        fourth,
      },
    })
  }, [item, variable])

  const titles = useMemo(() => {
    const actionDefinitionKey = variable?.key
    let sectionTitleOne = 'Localização'

    const generalTitle =
      item?.['socialName'] || item?.['name'] || item?.[variable?.basicSelect?.[0] ?? 'name'] || 'Informações gerais'

    const sectionTitleTwo = resolveSectionTitle(actionDefinitionKey, variable?.title)

    return {
      general: generalTitle,
      sectionOne: sectionTitleOne,
      sectionTwo: sectionTitleTwo,
      sectionThree: 'Mais informações',
    }
  }, [item, variable])

  return (
    <>
      {isOpen && information && titles && (
        <GeneralPageInformation
          isOpen={isOpen}
          onClose={onClose}
          isLoading={isLoading}
          general={information?.generalInfo as ObjectInfo}
          sectionOne={(information?.sectionOneInfo as ObjectInfo) || null}
          sectionTwo={(information?.sectionTwoInfo as ObjectInfo) || null}
          sectionThree={(information?.sectionThreeInfo as ObjectInfo) || null}
          title={titles?.general as string}
          sectionOneTitle={titles?.sectionOne as string}
          sectionTwoTitle={titles?.sectionTwo as string}
          sectionThreeTitle={titles?.sectionThree as string}
          markerCoordinates={coordinates}
        />
      )}
    </>
  )
}
